/* eslint-disable  */
import { useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useResolution886Report } from '@/context/Resolution886Report';
import { useApplicationContext } from '@/context/v1/Application/context';
import {
	useGetResolution886Report,
	useCreateResolution886Report,
	useUpdateResolution886Report,
	useGetResolution886ReportReview
} from '@/hooks/v2/resolution-886';
import { useStepControl } from '.';
import { StepsResolution886 } from '../../../enums';
import { TOAST_DURATION_RESOLUTION_886 } from '@/utils/constants';
import type { PreviousReview, Resolution886ReportResponse, ReviewResponse } from '@/context/Resolution886Report/types';

interface CreateResolution886ReportParams {
	name: string;
	workstation_id: string;
	sector_id: string;
	line_id: string;
	company_id: string;
	organization_id: string;
	review_id: string;
	employer_id: string;
	evaluator_id: string;
	occupational_doctor_id: string;
	safety_and_hygiene_responsible_id: string;
	workers_number: number;
	symptom_location: string;
	early_manifestation: boolean;
	written_work_procedure: boolean;
	training: boolean;
	worker_list_id: string[];
	file_list_id: string[];
}

interface UpdateResolution886ReportParams extends CreateResolution886ReportParams {
	id: string;
	review_id: string;
}

interface UseParamsProps {
	report_id: string;
}

const DEFAULT_FILES_QTY = 0;
const DEFAULT_LAST_REVIEW = 1;
const DEFAULT_PREVIOUS_REVIEW = 2;

export function useHandleActions() {
	const { report_id } = useParams<UseParamsProps>();

	const [filesQty, setFilesQty] = useState<number>(DEFAULT_FILES_QTY);
	const [createUpdateReport, setCreateUpdateReport] = useState<boolean>(false);
	const [isEnabledMessageFilesQty, setIsEnabledMessageFilesQty] = useState<boolean>(false);
	const [isEnabledNotification, setIsEnabledNotification] = useState<boolean>(false);

	const location = useLocation();
	const newReview = useQuery().get('newReview');

	const { organization, company } = useApplicationContext();
	const { isLoading: isLoadingCreateReport, mutateAsync: createResolution886Report } = useCreateResolution886Report();
	const { isLoading: isLoadingUpdateReport, mutateAsync: updateResolution886Report } = useUpdateResolution886Report();
	const {
		form,
		report,
		lastReviewId,
		lastReview,
		handleNextStep,
		handlePreviousStep,
		handleSetReport,
		handleSetLastReviewId,
		setInitialValues,
		mapDataToInitialValues,
		handleSetNewReview,
		handleSetLastReview,
		handleCurrentStep,
		setNewReviewCreated,
		handleSetPreviousReview,
		handleSetReviewConsolidated
	} = useResolution886Report();

	const { handleUpdateStep } = useStepControl();

	const {
		data,
		isFetching: isFetchingReport,
		refetch: refetchReport
	} = useGetResolution886Report({
		organization_id: organization?.id!,
		company_id: company?.id!,
		id: report_id || report?.id!
	});

	const {
		data: review,
		isFetching: isFetchingReview,
		refetch: refetchReview
	} = useGetResolution886ReportReview({
		organization_id: organization.id!,
		company_id: company.id!,
		report_id: data?.id!,
		review_id: lastReviewId!
	});

	function useQuery() {
		return new URLSearchParams(location.search);
	}

	async function handleCreateResolution886Report(values: CreateResolution886ReportParams): Promise<void> {
		const { new_file_list_id } = form.getFieldsValue(['new_file_list_id']);

		await createResolution886Report({ ...values, file_list_id: [...new_file_list_id] }).then(({ data }) => {
			refetchReport();
			handleSetReport(data as unknown as Resolution886ReportResponse);
			setCreateUpdateReport(true);
		});
	}

	async function handleUpdateResolution886Report(values: UpdateResolution886ReportParams) {
		const { new_file_list_id } = form.getFieldsValue(['new_file_list_id']);

		await updateResolution886Report({ ...values, file_list_id: [...new_file_list_id] }).then(({ data }) => {
			if (newReview === 'true' && !values.review_id) {
				setNewReviewCreated(true);
			}
			refetchReport();
			handleSetReport(data as unknown as Resolution886ReportResponse);
			setCreateUpdateReport(true);
			handleSetLastReviewId(data.reviews[data.reviews.length - DEFAULT_LAST_REVIEW].id);
			refetchReview();
		});
	}

	function handleValidateFileList(): void {
		const { file_list_id } = form.getFieldsValue(['file_list_id']);

		if (!file_list_id.length) {
			setIsEnabledNotification(true);
			setTimeout(() => setIsEnabledNotification(false), TOAST_DURATION_RESOLUTION_886);
			return;
		}

		if (newReview === 'true') {
			if (file_list_id.length !== lastReview?.assessments.length) {
				setFilesQty(lastReview?.assessments.length || DEFAULT_FILES_QTY);
				setIsEnabledMessageFilesQty(true);
				setTimeout(() => setIsEnabledMessageFilesQty(false), TOAST_DURATION_RESOLUTION_886);
				return;
			}
		}

		setIsEnabledNotification(false);
		setIsEnabledMessageFilesQty(false);

		report.id && !newReview ? handleUpdateStep(StepsResolution886.GENERAL_INFORMATION) : handleNextStep();
	}

	function setFileListId(review: ReviewResponse) {
		const file_list = review?.files?.map((file) => file.id);
		const new_file_list = form.getFieldValue(['new_file_list_id']);

		if (!new_file_list.length) {
			form.setFieldsValue({
				new_file_list_id: file_list
			});
		}
	}

	useEffect(() => {
		if (!isFetchingReport && !isFetchingReview) {
			if (createUpdateReport && data && review) {
				handleUpdateStep(StepsResolution886.ASSESSMENT, false);
			}

			if (report && review) {
				setInitialValues(
					mapDataToInitialValues(
						report as unknown as Resolution886ReportResponse,
						review as unknown as ReviewResponse,
						newReview ? true : false
					)
				);
			}

			if (newReview) {
				handleSetLastReview(review as unknown as ReviewResponse);
				handleSetNewReview();
			}

			if (data) {
				handleSetReport(data as unknown as Resolution886ReportResponse);
				handleSetLastReviewId(data.reviews[data.reviews.length - DEFAULT_LAST_REVIEW].id);
				handleSetPreviousReview(
					data.reviews[data.reviews.length - DEFAULT_PREVIOUS_REVIEW] as unknown as PreviousReview
				);

				if (report && review && !newReview) {
					handleSetReviewConsolidated(Number(review.consolidated) === 1);
					setFileListId(data.reviews[data.reviews.length - DEFAULT_LAST_REVIEW] as unknown as ReviewResponse);
					handleCurrentStep(data.reviews[data.reviews.length - DEFAULT_LAST_REVIEW].step!);
				}
			}
		}
	}, [
		data,
		report,
		review,
		newReview,
		createUpdateReport,
		isFetchingReview,
		isFetchingReport,
		handleNextStep,
		handleSetReport,
		handleSetLastReviewId
	]);

	const isLoading = isLoadingCreateReport || isLoadingUpdateReport || isFetchingReport || isFetchingReview;

	return {
		filesQty,
		isLoading,
		isEnabledMessageFilesQty,
		isEnabledNotification,
		handleCreateResolution886Report,
		handleUpdateResolution886Report,
		handlePreviousStep,
		handleValidateFileList,
		handleNextStep
	};
}
